import { defineStore } from 'pinia'
import type { ReserveItemReq, ReserveRes } from '@/composables/useOccurrenceHoldFlow'
import { parseISOToMs } from '@/utils/datetime'

export type CheckoutState = {
  occurrenceId: number | null
  holdsBySeat: Record<number, number> // seatId -> holdId
  expiresAt: string | null
  orderId: string | null
  occurrenceMeta: {
    title: string
    poster: string | null
    ageRating?: string
    venueName?: string
    hallName?: string
    startAtIso?: string
  } | null
  itemsPayload: ReserveItemReq[]
  total: string | null
  currency: string | null
}

export const useCheckoutStore = defineStore('checkout', {
  state: (): CheckoutState => ({
    occurrenceId: null,
    holdsBySeat: {},
    expiresAt: null,
    orderId: null,
    occurrenceMeta: null,
    itemsPayload: [],
    total: null,
    currency: null,
  }),

  getters: {
    isExpired(state: CheckoutState): boolean {
      if (!state.expiresAt) return true
      const ts = parseISOToMs(state.expiresAt)
      if (!ts) return true
      return ts <= Date.now()
    },
  },

  actions: {
    startOccurrence(occurrenceId: number) {
      if (this.occurrenceId !== occurrenceId) {
        this.resetCheckout()
      }
      this.occurrenceId = occurrenceId
    },

    upsertHold(seatId: number, holdId: number, expiresAt: string) {
      this.holdsBySeat[seatId] = holdId
      this.expiresAt = expiresAt
    },

    setOccurrenceMeta(meta: NonNullable<CheckoutState['occurrenceMeta']>) {
      this.occurrenceMeta = meta
    },

    removeSeatHold(seatId: number) {
      if (seatId in this.holdsBySeat) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.holdsBySeat[seatId]
      }

      if (Object.keys(this.holdsBySeat).length === 0 && !this.orderId) {
        this.expiresAt = null
      }
    },

    setReserve(resp: ReserveRes) {
      this.orderId = resp.order_id
      this.expiresAt = resp.expires_at

      if (typeof resp.total === 'string') this.total = resp.total
      if (typeof resp.currency === 'string') this.currency = resp.currency

      // persist for retry / refresh
      try {
        localStorage.setItem('lastOrderId', resp.order_id)
        localStorage.setItem('lastOrderExpiresAt', resp.expires_at)
      } catch {
        // ignore
      }
    },

    resetCheckout() {
      this.occurrenceId = null
      this.holdsBySeat = {}
      this.expiresAt = null
      this.orderId = null
      this.occurrenceMeta = null
      this.itemsPayload = []
      this.total = null
      this.currency = null
    },
  },
})

